import { ArrowDown, ArrowUp, Plus, Rows3, Trash2, X } from 'lucide-react';
import { useFormContext, type UseFieldArrayReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { SizeSetSystem } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import {
  EQUIVALENT_SYSTEMS,
  MAX_SIZES,
  type SizeSetFormInput,
  type SizeSetFormValues,
} from '../schemas/size-set-form-schema';

export interface SizeSetSizesSectionProps {
  sizes: UseFieldArrayReturn<SizeSetFormInput, 'values'>;
  /** Systems shown as equivalent columns, in order. */
  columns: readonly SizeSetSystem[];
  onAddColumn: (system: SizeSetSystem) => void;
  onRemoveColumn: (system: SizeSetSystem) => void;
  onAddRange: () => void;
  readOnly: boolean;
}

/** The ordered sizes of a set as an editable grid: one row per size, one column per equivalent. */
export function SizeSetSizesSection({
  sizes,
  columns,
  onAddColumn,
  onRemoveColumn,
  onAddRange,
  readOnly,
}: SizeSetSizesSectionProps) {
  const { t } = useTranslation('sizeSets');
  const form = useFormContext<SizeSetFormInput, unknown, SizeSetFormValues>();
  const system = form.watch('system');
  const { fields, append, move, remove } = sizes;
  const addable = EQUIVALENT_SYSTEMS.filter((s) => s !== system && !columns.includes(s));
  const listError = form.formState.errors.values;
  const listMessage = listError?.message ?? listError?.root?.message;
  const cell = (column: string, index: number) => t('sizes.cell', { column, row: index + 1 });

  return (
    <FormSection title={t('sizes.title')} description={t('sizes.description')}>
      {fields.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
          {t('sizes.empty')}
        </p>
      ) : (
        <div className="-mx-6 overflow-x-auto">
          <Table aria-label={t('sizes.title')} className="min-w-max">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 pl-6">{t('sizes.position')}</TableHead>
                <TableHead>{t('sizes.value')}</TableHead>
                <TableHead>{t('sizes.label')}</TableHead>
                <TableHead>{t('sizes.numericValue')}</TableHead>
                {columns.map((column) => (
                  <TableHead key={column}>
                    <span className="inline-flex items-center gap-1">
                      {t(`system.${column}`)}
                      {readOnly ? null : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="size-6"
                          aria-label={t('sizes.removeColumn', { system: t(`system.${column}`) })}
                          onClick={() => onRemoveColumn(column)}
                        >
                          <X aria-hidden />
                        </Button>
                      )}
                    </span>
                  </TableHead>
                ))}
                {readOnly ? null : (
                  <TableHead className="pr-6 text-right">{t('sizes.actions')}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell className="text-muted-foreground pl-6 align-top text-xs tabular-nums">
                    <span className="inline-flex h-8 items-center">{index + 1}</span>
                  </TableCell>
                  <TableCell className="align-top">
                    <FormField
                      control={form.control}
                      name={`values.${index}.value`}
                      render={({ field: input }) => (
                        <FormItem className="gap-1">
                          <FormControl>
                            <Input
                              {...input}
                              aria-label={cell(t('sizes.value'), index)}
                              className="h-8 w-24 font-mono text-[13px]"
                            />
                          </FormControl>
                          <FormMessage className="max-w-40 text-xs" />
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell className="align-top">
                    <FormField
                      control={form.control}
                      name={`values.${index}.label`}
                      render={({ field: input }) => (
                        <FormItem className="gap-1">
                          <FormControl>
                            <Input
                              {...input}
                              aria-label={cell(t('sizes.label'), index)}
                              className="h-8 w-28"
                            />
                          </FormControl>
                          <FormMessage className="max-w-40 text-xs" />
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell className="align-top">
                    <FormField
                      control={form.control}
                      name={`values.${index}.numericValue`}
                      render={({ field: input }) => (
                        <FormItem className="gap-1">
                          <FormControl>
                            <Input
                              {...input}
                              inputMode="decimal"
                              aria-label={cell(t('sizes.numericValue'), index)}
                              className="h-8 w-20 tabular-nums"
                            />
                          </FormControl>
                          <FormMessage className="max-w-40 text-xs" />
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column} className="align-top">
                      <FormField
                        control={form.control}
                        name={`values.${index}.equivalents.${column}`}
                        render={({ field: input }) => (
                          <FormItem className="gap-1">
                            <FormControl>
                              <Input
                                {...input}
                                value={input.value ?? ''}
                                aria-label={cell(
                                  t('sizes.equivalent', { system: t(`system.${column}`) }),
                                  index,
                                )}
                                className="h-8 w-20"
                              />
                            </FormControl>
                            <FormMessage className="max-w-40 text-xs" />
                          </FormItem>
                        )}
                      />
                    </TableCell>
                  ))}
                  {readOnly ? null : (
                    <TableCell className="pr-6 text-right align-top">
                      <div className="flex justify-end gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('sizes.moveUp', { row: index + 1 })}
                          disabled={index === 0}
                          onClick={() => move(index, index - 1)}
                        >
                          <ArrowUp aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('sizes.moveDown', { row: index + 1 })}
                          disabled={index === fields.length - 1}
                          onClick={() => move(index, index + 1)}
                        >
                          <ArrowDown aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={t('sizes.remove', { row: index + 1 })}
                          onClick={() => remove(index)}
                        >
                          <Trash2 aria-hidden />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {listMessage ? (
        <p role="alert" className="text-destructive text-sm">
          {listMessage}
        </p>
      ) : null}
      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= MAX_SIZES}
            onClick={() => append({ value: '', label: '', numericValue: '', equivalents: {} })}
          >
            <Plus aria-hidden />
            {t('sizes.add')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= MAX_SIZES}
            onClick={onAddRange}
          >
            <Rows3 aria-hidden />
            {t('sizes.addRange')}
          </Button>
          {addable.length > 0 ? (
            <Select
              value=""
              onValueChange={(value) => {
                const picked = addable.find((s) => s === value);
                if (picked) onAddColumn(picked);
              }}
            >
              <SelectTrigger size="sm" className="w-auto" aria-label={t('sizes.addColumn')}>
                <SelectValue placeholder={t('sizes.addColumn')} />
              </SelectTrigger>
              <SelectContent>
                {addable.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`system.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      )}
    </FormSection>
  );
}
